import { api, readCookie } from "@/lib/api"
import { bootstrapAuth } from "@/lib/bootstrap-auth"

// S6 5b/B1: server-side impersonation orchestration. The frontend makes NO
// authorization decision here — it only calls the endpoints and re-reads its
// own identity from /auth/me. Every privilege check lives server-side and reads
// the unforgeable httpOnly JWT cookie, which JS cannot read or modify.

// Enter impersonation. POST /super/clients/:id/impersonate is gated server-side
// by requireRole("SUPER_ADMIN") (only a genuine super session can call it); it
// mints a 1h CLIENT_ADMIN impersonation token, sets it as the httpOnly
// auth_token cookie, and sets the non-httpOnly `impersonation` banner cookie.
// We then re-bootstrap from /auth/me so the store reflects the impersonated
// client (isImpersonation:true). The caller navigates on success.
export async function enterImpersonation(clientId: string): Promise<void> {
  await api.post(`/super/clients/${clientId}/impersonate`)
  await bootstrapAuth()
}

// Exit impersonation. POST /auth/impersonate/exit performs the
// SECURITY-CRITICAL check server-side (the current token must be a genuine
// impersonation token AND impersonatedBy must resolve to a real, currently
// ACTIVE super admin) before minting a fresh super token, setting it as the
// auth_token cookie, and clearing the banner cookie. The frontend cannot skip
// or spoof that check: isImpersonation/impersonatedBy live inside the httpOnly
// JWT, unreadable/unwritable by JS. We then re-bootstrap from /auth/me (now the
// restored super admin). The caller navigates on success.
export async function exitImpersonation(): Promise<void> {
  await api.post("/auth/impersonate/exit")
  await bootstrapAuth()
}

// Banner display data, read from the non-httpOnly `impersonation` cookie the
// enter endpoint set (URL-encoded JSON {name,email}). Returns null when not
// impersonating (cookie absent or malformed).
export function readImpersonationBanner(): { name: string; email: string } | null {
  const raw = readCookie("impersonation")
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { name?: unknown; email?: unknown }
    if (typeof parsed.name === "string" && typeof parsed.email === "string") {
      return { name: parsed.name, email: parsed.email }
    }
    return null
  } catch {
    return null
  }
}
